import React, { useRef, useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications, NotificationDoc } from '../../../hooks/useNotifications';
import { useRestaurant } from '../../../providers/RestaurantContext';

const MAX_VISIBLE = 20;

function formatTime(ts: { toDate?: () => Date } | null): string {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
}

export const NotificationBell: React.FC = () => {
  const { restaurant } = useRestaurant();
  const { list, unreadCount, markAsRead } = useNotifications(restaurant?.id);
  const [open, setOpen] = useState(false);
  const prevOpenRef = useRef(false);
  const idsToMarkRef = useRef<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  const visibleList = list.filter((n) => !n.read).slice(0, MAX_VISIBLE);

  const visibleIdsKey = visibleList.map((n) => n.id).join(',');
  useEffect(() => {
    if (open) {
      idsToMarkRef.current = visibleList.map((n) => n.id);
    }
    if (prevOpenRef.current && !open && idsToMarkRef.current.length > 0) {
      markAsRead(idsToMarkRef.current);
      idsToMarkRef.current = [];
    }
    prevOpenRef.current = open;
  }, [open, markAsRead, visibleIdsKey]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!restaurant?.id) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-full bg-zinc-900/50 backdrop-blur-md border border-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[min(70vh,400px)] overflow-hidden rounded-xl bg-zinc-900 border border-white/10 shadow-xl z-50 flex flex-col">
          <div className="px-3 py-2 border-b border-white/5 text-sm font-semibold text-white">
            Notificaciones
          </div>
          <div className="overflow-y-auto flex-1">
            {visibleList.length === 0 ? (
              <div className="px-3 py-6 text-center text-zinc-500 text-sm">
                No hay notificaciones nuevas
              </div>
            ) : (
              visibleList.map((n) => (
                <NotificationItem key={n.id} item={n} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function NotificationItem({ item }: { item: NotificationDoc }) {
  const isPaid = item.type === 'mesa_paid';
  const label = isPaid
    ? `Mesa ${item.numeroMesa || item.mesaId} pagada por completo`
    : `Pago en mesa ${item.numeroMesa || item.mesaId}${item.total != null ? ` — ${formatCurrency(item.total)}` : ''}`;
  return (
    <div className="px-3 py-2.5 border-b border-white/5 last:border-b-0 text-sm text-zinc-300">
      {label}
      {item.createdAt && (
        <div className="text-[10px] text-zinc-500 mt-0.5">
          {formatTime(item.createdAt)}
        </div>
      )}
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default NotificationBell;
